import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withReset,
} from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  withComputed,
  withMethods,
  withProps,
  withState
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AlertService } from '@seatix/shared-util-alert';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';

export type PasswordResetState = {
  isTokenValid: boolean;
  tokenEmail: string | null;
  isTokenConsumed: boolean;
  remainingAttempts: number | null;
  retryAfter: number | null;
};

export function withPasswordReset() {
  return signalStoreFeature(
    withState<PasswordResetState>({
      isTokenValid: false,
      tokenEmail: null,
      isTokenConsumed: false,
      remainingAttempts: null,
      retryAfter: null,
    }),
    withProps(() => ({
      _alertService: inject(AlertService),
    })),
    withCallState({ collection: 'passwordResetRequest' }),
    withCallState({ collection: 'passwordReset' }),
    withCallState({ collection: 'validateResetToken' }),
    withComputed((store) => {
      const canRequestReset = computed(() => {
        const isLoading = store.passwordResetRequestLoading();
        const retryAfter = store.retryAfter();
        return !isLoading && (retryAfter === null || retryAfter <= 0);
      });

      const canResetPassword = computed(() => {
        const isLoading = store.passwordResetLoading();
        const tokenValid = store.isTokenValid();
        const tokenConsumed = store.isTokenConsumed();
        return !isLoading && tokenValid && !tokenConsumed;
      });

      return { canRequestReset, canResetPassword };
    }),
    withMethods((store) => {
      const authApi = inject(AuthApiService);

      function parseRateLimitError(error: unknown): {
        remainingAttempts: number | null;
        retryAfter: number | null;
      } {
        if (
          error &&
          typeof error === 'object' &&
          'error' in error &&
          error.error &&
          typeof error.error === 'object' &&
          'error' in error.error &&
          error.error.error === 'rate_limit_exceeded'
        ) {
          const errorData = error.error as Record<string, unknown>;
          return {
            remainingAttempts:
              typeof errorData['remaining_attempts'] === 'number'
                ? errorData['remaining_attempts']
                : null,
            retryAfter:
              typeof errorData['retry_after'] === 'number'
                ? errorData['retry_after']
                : null,
          };
        }
        return { remainingAttempts: null, retryAfter: null };
      }

      const requestPasswordReset = rxMethod<{
        email: string;
        turnstileToken: string;
      }>(
        pipe(
          tap(() => {
            patchState(store, {
              ...setLoading('passwordResetRequest'),
              remainingAttempts: null,
              retryAfter: null,
            });
          }),
          switchMap(({ email, turnstileToken }) =>
            authApi.requestPasswordReset(email, turnstileToken).pipe(
              tap(() => {
                patchState(store, setLoaded('passwordResetRequest'));
              }),
              catchError((error) => {
                const rateLimitInfo = parseRateLimitError(error);
                const errorMessage =
                  error?.error?.message || 'Failed to request password reset';
                patchState(store, {
                  ...setError(errorMessage, 'passwordResetRequest'),
                  ...rateLimitInfo,
                });
                return EMPTY;
              }),
            ),
          ),
        ),
      );

      const validateResetToken = rxMethod<{
        token: string;
        email: string;
      }>(
        pipe(
          tap(() =>
            patchState(store, {
              isTokenValid: false,
              tokenEmail: null,
              isTokenConsumed: false,
              ...setLoading('validateResetToken'),
            }),
          ),
          switchMap(({ email, token }) =>
            authApi.validateResetToken(token, email).pipe(
              tap((response) =>
                patchState(store, {
                  isTokenValid: response.data.valid,
                  tokenEmail: response.data.email,
                  ...setLoaded('validateResetToken'),
                }),
              ),
              catchError(() => {
                patchState(store, {
                  isTokenValid: false,
                  ...setError(
                    'Token expirado o inválido',
                    'validateResetToken',
                  ),
                });
                return EMPTY;
              }),
            ),
          ),
        ),
      );

      const resetPassword = rxMethod<{
        email: string;
        token: string;
        password: string;
        password_confirmation: string;
        turnstileToken: string;
      }>(
        pipe(
          tap(() => {
            patchState(store, setLoading('passwordReset'));
          }),
          switchMap(
            ({
              email,
              token,
              password,
              password_confirmation,
              turnstileToken,
            }) =>
              authApi
                .resetPassword(
                  { email, token, password, password_confirmation },
                  turnstileToken,
                )
                .pipe(
                  tap(() => {
                    patchState(store, {
                      ...setLoaded('passwordReset'),
                      isTokenConsumed: true,
                    });
                  }),
                  catchError((error) => {
                    const errorMessage =
                      error?.error?.message ||
                      'No se puedo restablecer la contraseña';
                    patchState(store, setError(errorMessage, 'passwordReset'));
                    return EMPTY;
                  }),
                ),
          ),
        ),
      );

      function clearPasswordResetState() {
        patchState(store, {
          isTokenValid: false,
          tokenEmail: null,
          isTokenConsumed: false,
          remainingAttempts: null,
          retryAfter: null,
        });
      }

      return {
        requestPasswordReset,
        validateResetToken,
        resetPassword,
        clearPasswordResetState,
      };
    }),
    withReset(),
  );
}
